/**
 * CustomerMobileCard Component
 * Mobile-optimized card layout for customer list
 */

import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, LocalShipping as TruckIcon } from '@mui/icons-material';
import type { Customer } from '../../../types/customer';
import { getInitials } from '../../../utils/formatters';

interface CustomerMobileCardProps {
  customer: Customer;
  onRowClick: (customerId: string) => void;
  onDeleteClick: (event: React.MouseEvent, customer: Customer) => void;
}

export const CustomerMobileCard: React.FC<CustomerMobileCardProps> = ({
  customer,
  onRowClick,
  onDeleteClick,
}) => {
  return (
    <Card
      onClick={() => onRowClick(customer.id)}
      sx={{
        mb: 2,
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '#f9fafb',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        },
        '&:active': {
          backgroundColor: '#f3f4f6',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header: Avatar, Name, and Delete Button */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: '#111827',
              fontSize: '0.875rem',
              fontWeight: 600,
              mr: 1.5,
            }}
          >
            {getInitials(customer.name)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: '#111827',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {customer.name}
            </Typography>

            {customer.email && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.8125rem',
                  color: '#6b7280',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {customer.email}
              </Typography>
            )}

            {customer.address && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  display: 'block',
                  fontStyle: 'italic',
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {customer.address}
              </Typography>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={(e) => onDeleteClick(e, customer)}
            sx={{
              ml: 1,
              backgroundColor: '#ef4444',
              color: 'white',
              width: 40,
              height: 40,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#dc2626',
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Truck Count Badge */}
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={<TruckIcon sx={{ fontSize: 16 }} />}
            label={`${customer.truckCount} Truck${customer.truckCount !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: '#3b82f6',
              color: 'white',
              fontWeight: 500,
              fontSize: '0.8125rem',
              height: 28,
              '& .MuiChip-icon': {
                color: 'white',
              },
            }}
          />
        </Box>

        {/* Service Due Information */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: '#6b7280',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              mb: 1,
              display: 'block',
            }}
          >
            Service Schedule
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {/* 2 Weeks - RED */}
            {customer.trucksDueWithin2Weeks > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#dc2626',
                    mr: 0.5,
                  }}
                >
                  {customer.trucksDueWithin2Weeks}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#991b1b',
                    fontWeight: 500,
                  }}
                >
                  within 2w
                </Typography>
              </Box>
            )}

            {/* 2-6 Weeks - ORANGE */}
            {customer.trucksDueWithin2To6Weeks > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#fff7ed',
                  border: '1px solid #fed7aa',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#f97316',
                    mr: 0.5,
                  }}
                >
                  {customer.trucksDueWithin2To6Weeks}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#c2410c',
                    fontWeight: 500,
                  }}
                >
                  in 2-6w
                </Typography>
              </Box>
            )}

            {/* 6-12 Weeks - GREEN */}
            {customer.trucksDueWithin6To12Weeks > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#16a34a',
                    mr: 0.5,
                  }}
                >
                  {customer.trucksDueWithin6To12Weeks}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#15803d',
                    fontWeight: 500,
                  }}
                >
                  in 6-12w
                </Typography>
              </Box>
            )}

            {/* No service due */}
            {customer.trucksDueWithin2Weeks === 0 &&
              customer.trucksDueWithin2To6Weeks === 0 &&
              customer.trucksDueWithin6To12Weeks === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.8125rem',
                    color: '#9ca3af',
                    fontStyle: 'italic',
                  }}
                >
                  No service due soon
                </Typography>
              )}
          </Stack>
        </Box>

        {/* Notes (if available) */}
        {customer.notes && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid #f3f4f6',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: '#6b7280',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {customer.notes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerMobileCard;
